import type { KeyboardEvent } from 'react';
import { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { TextInput, tokens, Alert } from '@hotjar/hotsauce';
import { validateEmail } from '@hotjar/webapp/lib/email';
import { LoginLayout } from '@hotjar/webapp/features/Login';
import { Form, Title, StyledField } from '@hotjar/webapp/features/Login/styles';
import { ErrorText } from '@hotjar/webapp/components/ErrorText';

import { navigateTo } from '@hotjar/webapp/utils/routing';
import { LOGIN } from '@hotjar/webapp/constants/routes/auth';
import { useTrack } from '@hotjar/tracking';
import type { ForgotPasswordResponseDTO } from './api';
import { forgotPassword } from './api';
import { BackLoginButton, Subheader, SendEmailButton, Content } from './forgot-password-styles';

const TRACKING_LABEL = 'Forgot password';

export const ForgotPasswordPage = (): JSX.Element => {
  const [email, setEmail] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [emailSent, setEmailSent] = useState<boolean>(false);

  const { t, i18n } = useTranslation();
  const currentLocale = i18n.languages?.[0];

  const { formSubmit } = useTrack();

  const sendResetMail = () => {
    if (!validateEmail(email)) {
      setError(t('common:login.form.invalid-email'));
      return;
    }
    setIsLoading(true);
    forgotPassword(email, currentLocale)
      .then((data: ForgotPasswordResponseDTO) => {
        if (data.success) {
          setEmailSent(true);
          formSubmit(TRACKING_LABEL, true, {}, {});
        } else if (data.error) {
          setError(data.error.email[0]);
          formSubmit(TRACKING_LABEL, false, {}, { error: data.error });
        }
      })
      .catch(() => {
        formSubmit(TRACKING_LABEL, false, {}, {});
      })
      .finally(() => setIsLoading(false));
  };

  // "alertContent: any" is a workaround for the problem, where @hotsauce's <Alert />
  // expects "string" as a children, rather than "ReactNode"
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const alertContent: any = (
    <Trans i18nKey="password.forgot-password.alert-text" values={{ email }}>
      An email is on the way to <b>{{ email }}</b> with instructions for resetting your password.
    </Trans>
  );
  const onChange = (newEmailValue: string): void => {
    setError('');
    setEmail(newEmailValue);
  };
  const content = (
    <Content>
      {emailSent ? (
        <>
          <Alert
            css={{ 'margin-bottom': tokens.spacer.x5 }}
            title={t('password.forgot-password.alert-title')}
            kind="info"
          >
            {alertContent}
          </Alert>
          <Subheader>
            <Trans i18nKey="password.forgot-password.instruction">
              {
                'To reset your password, follow the instructions in the email we&apos;ve just sent you from'
              }{' '}
              <b>noreply@hotjar.com</b>.
            </Trans>
          </Subheader>
        </>
      ) : (
        <>
          <StyledField
            size="l"
            labelText={t('password.forgot-password.email-input-label')}
            inputId="email"
          >
            <TextInput
              id="email"
              data-testid="email-input"
              placeholder={t('password.forgot-password.email-input-placeholder')}
              size="l"
              autoFocus
              isDisabled={isLoading}
              onChange={(e) => onChange(e.target.value)}
              onKeyPress={(event: KeyboardEvent) => event.key === 'Enter' && sendResetMail()}
              isInvalid={!!error}
            />
            {error && <ErrorText data-testid="error-message">{error}</ErrorText>}
          </StyledField>

          <SendEmailButton
            id="submit"
            isLoading={isLoading}
            isDisabled={!!error}
            data-testid="send-email-btn"
            onClick={sendResetMail}
            size="l"
          >
            {t('password.forgot-password.cta-btn')}
          </SendEmailButton>
        </>
      )}
    </Content>
  );

  return (
    <LoginLayout id="forgot-password-form">
      <Form onSubmit={(e) => e.preventDefault()}>
        <Title>{t('password.forgot-password.heading')}</Title>
        <Subheader>{t('password.forgot-password.sub-heading')}</Subheader>
        {content}
        <BackLoginButton
          icon="arrow_left"
          kind="ghost"
          data-testid="go-login-btn"
          onClick={() => navigateTo(LOGIN.path)}
        >
          {t('password.forgot-password.go-login')}
        </BackLoginButton>
      </Form>
    </LoginLayout>
  );
};
